import * as ImagePicker from 'expo-image-picker';
import useUnsavedChanges from '../../commons/unsavedChanges/useUnsavedChanges';
import useUnsavedFormState from '../../commons/unsavedChanges/useUnsavedFormState';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  addImage,
  GetProductDetail,
  UpdateProductInfo,
  saveProductDataLocally
} from '../../actions/productAction';
import { createInventoryReportItems } from '../../actions/inventoryReportActions';

const qunatityLabel1 = 'Previous QOH';
const qunatityLabel2 = 'New QOH';
const quantityMessage = 'Inventory QTY entered will replace existing QOH.';
const useRecountProduct = ({navigation, route}) => {
  const {steps} = route.params;
  const isManualMode = steps.includes('manual');
  const store = useSelector(state => state.product);
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();
  const [qtyReceive, setReceiveQty] = useState('');
  const [recevingNotes, setReceivingNotes] = useState(
    store.product.basicinfo.product_receiving_instructions
  );
  const [locationPrimary, setLocationPrimary] = useState(
    store.product.inventory_warehouses[0].info.location_primary
  );
  const [locationPrimaryQty, setLocationPrimaryQty] = useState(
    store.product.inventory_warehouses[0].info.location_primary_qty
  );
  const [locationSecondary, setLocationSecondary] = useState(
    store.product.inventory_warehouses[0].info.location_secondary
  );
  const [locationSecondaryQty, setLocationSecondaryQty] = useState(
    store.product.inventory_warehouses[0].info.location_secondary_qty
  );
  const [locationTertiary, setLocationTertiary] = useState(
    store.product.inventory_warehouses[0].info.location_tertiary
  );
  const [locationTertiaryQty, setLocationTertiaryQty] = useState(
    store.product.inventory_warehouses[0].info.location_tertiary_qty
  );
  const [customProduct1, setCustomProduct1] = useState(
    store.product?.basicinfo?.custom_product_1 ?? ''
  );
  const [customProduct2, setCustomProduct2] = useState(
    store.product?.basicinfo?.custom_product_2 ?? ''
  );
  const qtyRef = useRef();
  const locationRef = useRef();
  const locationPrimaryQtyRef = useRef();
  const locationSecondaryRef = useRef();
  const locationSecondaryQtyRef = useRef();
  const locationTertiaryRef = useRef();
  const locationTertiaryQtyRef = useRef();
  const receivingNotesRef = useRef();
  const customProduct1Ref = useRef();
  const customProduct2Ref = useRef();

  useEffect(() => {
    if (store.AddImage) {
      dispatch(GetProductDetail(store.product.basicinfo.id));
    }
  }, [store.AddImage]);

  useEffect(() => {
    if (isManualMode) {
      qtyRef.current?.focus();
    } else if (steps.includes('quantity')) {
      qtyRef.current.focus();
    } else if (steps.includes('location')) {
      locationRef.current.focus();
    }
  }, []);

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1
    });

    dispatch(addImage(store.product.basicinfo.id, result.uri));
  };

  const createInventoryReportItem = useCallback(async (product) => {
    try {
      const currentReportId = await AsyncStorage.getItem('current_report_id');
      const currentMode = await AsyncStorage.getItem('current_report_mode');

      if (currentReportId && currentMode) {
        const quantityToReceive = currentMode === 'recount'
          ? parseInt(qtyReceive || 0)
          : parseInt(qtyReceive || 0) - (product.inventory_warehouses[0].info.available_inv || 0);

        const discrepancy = currentMode === 'recount'
          ?  parseInt(qtyReceive || 0) - (product.inventory_warehouses[0].info.available_inv || 0)
          : parseInt(qtyReceive || 0);

        dispatch(createInventoryReportItems({
          id: currentReportId,
          name: product.basicinfo.name,
          quantity_to_receive: quantityToReceive,
          current_quantity_on_hand: product.inventory_warehouses[0].info.available_inv || 0,
          discrepancy: discrepancy,
          mode: currentMode,
          sku: product.skus?.[0]?.sku || '',
          product_id: product.basicinfo.id
        }));
      }
    } catch (error) {
      console.error('Error creating inventory report item:', error);
    }
  }, [qtyReceive, dispatch]);

  const saveQuantity = useCallback(async () => {
    if (!qtyReceive) return;
    const product = Object.assign({}, store.product);
    product.inventory_warehouses[0].info.quantity_on_hand =
      parseInt(qtyReceive);

    dispatch(UpdateProductInfo(product.basicinfo.id, product));
    await createInventoryReportItem(product);
  }, [qtyReceive, store.product, createInventoryReportItem]);
  const {hasUnsavedChanges, markSaved, revertUnsavedChanges} = useUnsavedFormState({
    recevingNotes: [recevingNotes, setReceivingNotes],
    locationPrimary: [locationPrimary, setLocationPrimary],
    locationPrimaryQty: [locationPrimaryQty, setLocationPrimaryQty],
    locationSecondary: [locationSecondary, setLocationSecondary],
    locationSecondaryQty: [locationSecondaryQty, setLocationSecondaryQty],
    locationTertiary: [locationTertiary, setLocationTertiary],
    locationTertiaryQty: [locationTertiaryQty, setLocationTertiaryQty],
    customProduct1: [customProduct1, setCustomProduct1],
    customProduct2: [customProduct2, setCustomProduct2],
  });

  const saveProductInfo = useCallback(async () => {
    const product = Object.assign({}, store.product);

    product.inventory_warehouses[0].info.location_primary = locationPrimary;
    product.inventory_warehouses[0].info.location_primary_qty = parseInt(
      locationPrimaryQty ?? 0
    );
    product.inventory_warehouses[0].info.location_secondary = locationSecondary;
    product.inventory_warehouses[0].info.location_secondary_qty = parseInt(
      locationSecondaryQty ?? 0
    );
    product.inventory_warehouses[0].info.location_tertiary = locationTertiary;
    product.inventory_warehouses[0].info.location_tertiary_qty = parseInt(
      locationTertiaryQty ?? 0
    );
    product.basicinfo.product_receiving_instructions = recevingNotes;
    product.basicinfo.custom_product_1 = customProduct1;
    product.basicinfo.custom_product_2 = customProduct2;

    dispatch(UpdateProductInfo(product.basicinfo.id, product));
    await createInventoryReportItem(product);
    markSaved();
  }, [
    locationPrimary,
    locationPrimaryQty,
    locationSecondary,
    locationSecondaryQty,
    locationTertiary,
    locationTertiaryQty,
    recevingNotes,
    customProduct1,
    customProduct2,
    createInventoryReportItem
  ]);

  const saveAll = useCallback(async () => {
    const source = store.product;
    const info = {...source.inventory_warehouses[0].info};

    if (qtyReceive) {
      info.quantity_on_hand = parseInt(qtyReceive);
    }

    info.location_primary = locationPrimary;
    info.location_primary_qty = parseInt(locationPrimaryQty ?? 0);
    info.location_secondary = locationSecondary;
    info.location_secondary_qty = parseInt(locationSecondaryQty ?? 0);
    info.location_tertiary = locationTertiary;
    info.location_tertiary_qty = parseInt(locationTertiaryQty ?? 0);

    const product = {
      ...source,
      basicinfo: {
        ...source.basicinfo,
        product_receiving_instructions: recevingNotes,
        custom_product_1: customProduct1,
        custom_product_2: customProduct2
      },
      inventory_warehouses: [
        {...source.inventory_warehouses[0], info},
        ...source.inventory_warehouses.slice(1)
      ]
    };

    dispatch(UpdateProductInfo(product.basicinfo.id, product));
    await createInventoryReportItem(product);
  }, [
    qtyReceive,
    store.product,
    locationPrimary,
    locationPrimaryQty,
    locationSecondary,
    locationSecondaryQty,
    locationTertiary,
    locationTertiaryQty,
    recevingNotes,
    customProduct1,
    customProduct2,
    createInventoryReportItem
  ]);

  const goBack = useCallback(() => {
    navigation.navigate('ScanRecount');
  }, []);
  const onPressProductDetail = useCallback(() => {
    navigation.navigate('ProductDetail', {
      productInfo: store.product.basicinfo.id,
      userData: user.userInfo,
      page: 'RecountProduct',
      oncomeBack: goBack
    });
  }, []);
  const unsavedChanges = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: saveProductInfo,
    onDiscard: revertUnsavedChanges,
    navigation
  });

  return {
    hasUnsavedChanges,
    unsavedChanges,
    navigation,
    user,
    steps,
    qunatityLabel1,
    qunatityLabel2,
    quantityMessage,
    qtyReceive,
    setReceiveQty,
    product: store.product,
    qtyRef,
    locationRef,
    locationPrimaryQtyRef,
    locationSecondaryRef,
    locationSecondaryQtyRef,
    locationTertiaryRef,
    locationTertiaryQtyRef,
    receivingNotesRef,
    customProduct1Ref,
    customProduct2Ref,
    isManualMode,
    saveQuantity,
    saveAll,
    locationPrimary,
    setLocationPrimary,
    locationPrimaryQty,
    setLocationPrimaryQty,
    locationSecondary,
    setLocationSecondary,
    locationSecondaryQty,
    setLocationSecondaryQty,
    locationTertiary,
    setLocationTertiary,
    locationTertiaryQty,
    setLocationTertiaryQty,
    customProduct1,
    setCustomProduct1,
    customProduct2,
    setCustomProduct2,
    pickImage,
    recevingNotes,
    setReceivingNotes,
    saveProductInfo,
    goBack,
    onPressProductDetail
  };
};
export default useRecountProduct;
